/**
 * iplicit Auto Forward console — admin-only, desktop.
 *
 * The old InfoHub card was built for a phone and showed outcomes. This is a wide
 * control view built to answer, at a glance: is the app running, is anything
 * stuck, and what happened to this invoice. It lands on "Needs attention"
 * because the first question every morning is whether anything needs you.
 *
 * Columns are declared per tab rather than shared, so no tab carries a column
 * that is permanently blank for its state.
 */
import { Fragment, useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'
import {
  EVENT_LABELS, FAIL_CATEGORIES, FOLDER_LABELS, VERDICT_LABELS, ensureFolder, getFolders,
  getInvoice, getInvoices, getNeedsAttention, getOverview, ignoreInvoice, linkExisting, money, patchReview,
  patchSettings, recheckInvoice, runNow, sendInvoice, since, stamp, unignoreInvoice, until,
  urgency, verifyPlacement,
  searchNominals, searchSuppliers, setNominal, setSupplier,
  type ApEvent, type ApMode, type ApSettings, type CategoryCount, type Counts, type FolderKey,
  type Invoice, type MailFolder, type NominalOption, type ReconcileStatus, type SupplierOption,
  type Verdict,
} from '../ap/api'

type TabKey = 'needs' | 'inflight' | 'confirmed' | 'inject' | 'failed' | 'ignored' | 'all'

interface Col {
  key: string
  label: string
  align?: 'right'
  wide?: boolean
  render: (inv: Invoice, ctx: { windowMinutes: number }) => ReactNode
}

const T = (iso: string | null): ReactNode => {
  const s = stamp(iso)
  return <span title={s.full}>{s.short}</span>
}

const cSupplier: Col = {
  key: 'supplier', label: 'Supplier',
  render: (i) => (
    <span>
      {i.supplier ?? <span className="muted">not extracted</span>}
      {i.dryRun && <span className="tag">dry run</span>}
      {i.isDuplicate && <span className="tag ap-tag-warn">possible duplicate</span>}
    </span>
  ),
}
const cDocNo: Col = { key: 'docno', label: 'Invoice no', render: (i) => <span className="mono">{i.theirDocNo ?? '—'}</span> }
const cTotal: Col = { key: 'total', label: 'Total', align: 'right', render: (i) => <span className="mono">{money(i.gross)}</span> }
const cFrom: Col = { key: 'from', label: 'From', render: (i) => <span className="ap-from">{i.senderEmail ?? '—'}</span> }
const cArrived: Col = { key: 'arrived', label: 'Arrived', render: (i) => T(i.arrivedUtc) }
const cForwarded: Col = { key: 'fwd', label: 'Forwarded', render: (i) => T(i.forwardedUtc) }
const cSettled: Col = { key: 'settled', label: 'Confirmed', render: (i) => T(i.settledUtc) }
const cFailedAt: Col = { key: 'failedat', label: 'Failed', render: (i) => T(i.failedUtc) }
const cInjected: Col = { key: 'injected', label: 'Injected', render: (i) => T(i.injectedUtc) }
const cDoc: Col = {
  key: 'doc', label: 'iplicit doc',
  render: (i) => (i.iplicitDocNo ? <span className="mono">{i.iplicitDocNo}</span> : <span className="muted">no proof</span>),
}
const cHow: Col = {
  key: 'how', label: 'How',
  render: (i) =>
    i.createMethod === 'inject' ? 'direct create'
    : i.createMethod === 'forward' ? 'forward'
    : i.createMethod === 'existing' ? 'was already there'
    : '—',
}
const cChecks: Col = {
  key: 'checks', label: 'Checks',
  render: (i) => (
    <span title={i.lastCheckUtc ? `last checked ${stamp(i.lastCheckUtc).full}` : 'never checked'}>
      {i.checks}
      {i.lastCheckUtc && <span className="muted"> · {since(i.lastCheckUtc)} ago</span>}
    </span>
  ),
}
const cAge: Col = {
  key: 'age', label: 'Age',
  render: (i, { windowMinutes }) => {
    const u = urgency(i, windowMinutes)
    return <span className={u === 'late' ? 'ap-age-late' : u === 'warn' ? 'ap-age-warn' : undefined}>{since(i.arrivedUtc)}</span>
  },
}
const cVerdict: Col = {
  key: 'verdict', label: 'Status',
  render: (i) => {
    if (!i.verdict) return <span className="muted">—</span>
    return (
      <span
        className="ap-verdict"
        data-v={i.verdict}
        title={i.verdictBy ? `Set by ${i.verdictBy}` : "The app's own assessment"}
      >
        {VERDICT_LABELS[i.verdict]}
        {i.verdictBy && <span className="ap-verdict-hand">✎</span>}
      </span>
    )
  },
}

const cSupplierAcct: Col = {
  key: 'acct', label: 'iplicit supplier',
  render: (i) => {
    const r = i.supplierResolution
    if (!r) return <span className="muted">not looked up</span>
    if (r.status === 'confident') {
      return <span title={`score ${r.score?.toFixed(2) ?? '?'}`}>{r.name}</span>
    }
    return (
      <span className="ap-unresolved" title={r.candidates.map((c) => `${c.description} (${c.score})`).join(' · ')}>
        {r.status === 'ambiguous' ? 'ambiguous' : 'no match'}
        {r.candidates[0] && <span className="muted"> · best: {r.candidates[0].description}</span>}
      </span>
    )
  },
}

const cState: Col = {
  key: 'state', label: 'State',
  render: (i) => <span className="ap-pill" data-state={i.state}>{i.state === 'InFlight' ? 'in flight' : i.state === 'AutoFailed' ? 'auto failed' : i.state.toLowerCase()}</span>,
}
const cWhere: Col = {
  key: 'where', label: 'Folder now',
  render: (i) => {
    if (i.placementMismatch) {
      return (
        <span className="ap-mismatch" title={`Expected "${i.folderExpectedName ?? '?'}" but Graph reports "${i.folderActualName ?? 'missing'}"`}>
          {i.folderActualName ?? 'missing'} <span className="ap-tag-warn">wrong folder</span>
        </span>
      )
    }
    if (!i.folderCheckedUtc) return <span className="muted">not checked</span>
    return <span title={`checked ${stamp(i.folderCheckedUtc).full}`}>{i.folderActualName ?? '—'}</span>
  },
}
const cWhy: Col = { key: 'why', label: 'Why', wide: true, render: (i) => <span className="ap-why">{i.failReason ?? '—'}</span> }
const cIgnoredAt: Col = { key: 'igat', label: 'Ignored', render: (i) => T(i.ignoredUtc) }
const cIgnoredBy: Col = { key: 'igby', label: 'By', render: (i) => <span className="ap-from">{i.ignoredBy ?? '—'}</span> }
const cIgnoreWhy: Col = { key: 'igwhy', label: 'Reason', wide: true, render: (i) => <span className="ap-why">{i.ignoreReason ?? '—'}</span> }

const TABS: Array<{ key: TabKey; label: string; blurb: string; cols: Col[] }> = [
  {
    key: 'needs', label: 'Needs attention',
    blurb: 'Stuck past the window, auto failed, in the wrong folder, or a possible duplicate.',
    cols: [cSupplier, cDocNo, cTotal, cFrom, cArrived, cAge, cVerdict, cSupplierAcct, cState, cChecks, cWhere, cWhy],
  },
  {
    key: 'inflight', label: 'In flight',
    blurb: 'Forwarded to iplicit and waiting to be confirmed. Red means the window has passed and the app is about to create it directly.',
    cols: [cSupplier, cDocNo, cTotal, cFrom, cArrived, cVerdict, cSupplierAcct, cForwarded, cAge, cChecks, cWhere],
  },
  {
    key: 'confirmed', label: 'Confirmed',
    blurb: 'Proven in iplicit. Every row carries the document number that proves it.',
    cols: [cSupplier, cDocNo, cTotal, cFrom, cArrived, cForwarded, cSettled, cHow, cDoc, cWhere],
  },
  {
    key: 'inject', label: 'Direct inject',
    blurb: 'Went past the window unconfirmed, so the app created the invoice in iplicit itself.',
    cols: [cSupplier, cDocNo, cTotal, cFrom, cArrived, cFailedAt, cInjected, cDoc, cState, cWhere],
  },
  {
    key: 'failed', label: 'Auto failed',
    blurb: 'The app could not get these into iplicit. Enter them by hand, then ignore the row.',
    cols: [cSupplier, cDocNo, cTotal, cFrom, cArrived, cVerdict, cForwarded, cChecks, cFailedAt, cWhy, cWhere],
  },
  {
    key: 'ignored', label: 'Ignored',
    blurb: 'Deliberately skipped. Reversible — un-ignore returns a row to the flow.',
    cols: [cSupplier, cDocNo, cTotal, cFrom, cArrived, cIgnoredAt, cIgnoredBy, cIgnoreWhy, cWhere],
  },
  {
    key: 'all', label: 'All',
    blurb: 'Everything, every column. Scrolls sideways.',
    cols: [cSupplier, cDocNo, cTotal, cFrom, cArrived, cVerdict, cSupplierAcct, cForwarded, cChecks, cFailedAt, cInjected, cSettled, cState, cHow, cDoc, cWhere, cWhy],
  },
]

// ─── settings dialog ─────────────────────────────────────────────────────────

function SettingsDialog({
  settings, onClose, onSaved,
}: {
  settings: ApSettings
  onClose: () => void
  onSaved: (s: ApSettings) => void
}) {
  const [folders, setFolders] = useState<MailFolder[] | null>(null)
  const [mailboxName, setMailboxName] = useState('')
  const [sourceCount, setSourceCount] = useState<number | null>(null)
  const [names, setNames] = useState<Record<FolderKey, string>>({
    source: settings.folders.source.name,
    review: settings.folders.review.name,
    done: settings.folders.done.name,
    failed: settings.folders.failed.name,
    ignored: settings.folders.ignored.name,
  })
  const [windowMinutes, setWindowMinutes] = useState(settings.windowMinutes)
  const [perRunCap, setPerRunCap] = useState(settings.perRunCap)
  const [busy, setBusy] = useState<string | null>(null)
  const [err, setErr] = useState<string | null>(null)
  const [msg, setMsg] = useState<string | null>(null)

  const load = useCallback(async () => {
    setErr(null)
    try {
      const r = await getFolders()
      setFolders(r.folders)
      setMailboxName(r.mailbox)
      setSourceCount(r.sourceFolderCount)
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e))
    }
  }, [])

  useEffect(() => { void load() }, [load])

  async function createOrBind(key: FolderKey) {
    setBusy(key); setErr(null); setMsg(null)
    try {
      const r = await ensureFolder(key, names[key].trim())
      onSaved(r.settings)
      setMsg(r.created ? `Created "${r.folder.name}" in ${mailboxName}.` : `Linked to the existing "${r.folder.name}".`)
      await load()
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(null)
    }
  }

  async function saveNumbers() {
    setBusy('numbers'); setErr(null); setMsg(null)
    try {
      const r = await patchSettings({ windowMinutes, perRunCap })
      onSaved(r.settings)
      setMsg('Saved.')
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(null)
    }
  }

  return (
    <div className="ap-modal-back" role="dialog" aria-modal="true" aria-label="iplicit Auto Forward settings">
      <div className="ap-modal">
        <div className="panel__head">
          <div>
            <h2>Mailbox settings</h2>
            <p className="panel__sub">
              {mailboxName ? <>Reading <span className="mono">{mailboxName}</span>. </> : null}
              The app reads <strong>only</strong> the processing folder. Nothing in the Inbox is ever touched.
            </p>
          </div>
          <button className="btn" type="button" onClick={onClose}>Close</button>
        </div>

        {err && <p className="alert alert--error">{err}</p>}
        {msg && <p className="alert alert--ok">{msg}</p>}

        {FOLDER_LABELS.map((f) => {
          const bound = settings.folders[f.key]
          const live = folders?.find((x) => x.id === bound.id)
          return (
            <div key={f.key} className="ap-folder-row">
              <div className="ap-folder-head">
                <label className="sched-label" htmlFor={`f-${f.key}`}>{f.label}</label>
                {f.key === 'source' && sourceCount !== null && (
                  <span className="ap-folder-count">{sourceCount} email{sourceCount === 1 ? '' : 's'} waiting</span>
                )}
              </div>
              <div className="ap-folder-controls">
                <input
                  id={`f-${f.key}`}
                  className="sched-input"
                  type="text"
                  value={names[f.key]}
                  disabled={busy !== null}
                  onChange={(e) => setNames((n) => ({ ...n, [f.key]: e.target.value }))}
                />
                <button className="btn" type="button" disabled={busy !== null || !names[f.key].trim()} onClick={() => void createOrBind(f.key)}>
                  {busy === f.key ? 'Working…' : bound.id ? 'Rename / relink' : 'Create or link'}
                </button>
                {bound.id
                  ? <span className="ap-bound">linked{live ? ` · ${live.totalItemCount} item${live.totalItemCount === 1 ? '' : 's'}` : ''}</span>
                  : <span className="ap-unbound">not linked yet</span>}
              </div>
              <p className="sched-help">{f.help}</p>
            </div>
          )
        })}

        <div className="ap-folder-row">
          <div className="ap-folder-controls">
            <label className="sched-label" htmlFor="ap-window">Give up after</label>
            <input id="ap-window" className="sched-input sched-input--num" type="number" min={5} max={1440}
              value={windowMinutes} disabled={busy !== null}
              onChange={(e) => setWindowMinutes(Number(e.target.value) || 5)} />
            <span className="muted">minutes, then create it directly</span>
          </div>
          <div className="ap-folder-controls">
            <label className="sched-label" htmlFor="ap-cap">Take at most</label>
            <input id="ap-cap" className="sched-input sched-input--num" type="number" min={1} max={200}
              value={perRunCap} disabled={busy !== null}
              onChange={(e) => setPerRunCap(Number(e.target.value) || 1)} />
            <span className="muted">emails per run</span>
          </div>
          <div className="sched-editor__actions">
            <button className="btn" type="button" disabled={busy !== null} onClick={() => void saveNumbers()}>
              {busy === 'numbers' ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>

        {folders && (
          <details className="ap-folder-list">
            <summary>All folders in this mailbox ({folders.length})</summary>
            <ul>
              {folders.map((f) => (
                <li key={f.id}>
                  {f.path} <span className="muted">· {f.totalItemCount} item{f.totalItemCount === 1 ? '' : 's'}</span>
                </li>
              ))}
            </ul>
          </details>
        )}
      </div>
    </div>
  )
}


/**
 * Supplier and nominal pickers.
 *
 * Shown when the matcher could not confidently resolve the supplier, which is not
 * a failure of the matcher so much as its correct behaviour: "Cleaning & Paper
 * Disposables Ltd" scores 0.50 against "CPD CLEANING AND PAPER", and accepting
 * that automatically would eventually pay the wrong company. A person decides, and
 * "Remember" means they only decide once per supplier.
 */
function SupplierPicker({
  invoice, onChanged,
}: {
  invoice: Invoice
  onChanged: (inv: Invoice, events: ApEvent[]) => void
}) {
  const [q, setQ] = useState(invoice.proposal?.supplier ?? invoice.supplier ?? '')
  const [opts, setOpts] = useState<SupplierOption[]>([])
  const [warning, setWarning] = useState<string | null>(null)
  const [chosen, setChosen] = useState<SupplierOption | null>(null)
  const [remember, setRemember] = useState(true)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  // Debounced so typing does not fire a query per keystroke.
  useEffect(() => {
    const t = setTimeout(() => {
      void (async () => {
        try {
          const r = await searchSuppliers(q)
          setOpts(r.suppliers)
          setWarning(r.warning ?? null)
        } catch (e) {
          setErr(e instanceof Error ? e.message : String(e))
        }
      })()
    }, 250)
    return () => clearTimeout(t)
  }, [q])

  const closest = invoice.supplierResolution?.status !== 'confident'
    ? (invoice.supplierResolution?.candidates ?? [])
    : []

  /**
   * Select one of the scored candidates.
   *
   * Rows resolved before the candidate list carried account ids fall back to a
   * lookup by code, so an invoice that has been sitting in review since before
   * this shipped is still one click rather than a re-check.
   */
  async function pickCandidate(c: { contactAccountId?: string; description: string; code: string }) {
    setErr(null)
    if (c.contactAccountId) {
      setChosen({ contactAccountId: c.contactAccountId, code: c.code, description: c.description, defaultNominal: null })
      setQ(c.description)
      return
    }
    setBusy(true)
    try {
      const r = await searchSuppliers(c.code || c.description)
      const hit = r.suppliers.find((s) => (c.code && s.code === c.code) || s.description === c.description)
      if (!hit) { setErr(`Could not find "${c.description}" in the cached supplier list — search for it instead.`); return }
      setChosen(hit)
      setQ(hit.description)
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  async function apply() {
    if (!chosen) { setErr('Pick a supplier from the list first.'); return }
    setBusy(true); setErr(null)
    try {
      const r = await setSupplier(invoice.id, {
        contactAccountId: chosen.contactAccountId,
        name: chosen.description,
        // Whatever the nominal control currently holds, so remembering a supplier
        // captures its coding too.
        nominalCode: invoice.nominalCode,
        remember,
      })
      onChanged(r.invoice, r.events)
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="ap-picker">
      <h4>Choose the iplicit supplier</h4>
      {warning && <p className="alert alert--error">{warning}</p>}
      {err && <p className="alert alert--error">{err}</p>}

      {closest.length > 0 && (
        <div className="ap-closest">
          <span className="sched-label">Closest</span>
          <ul className="ap-picker-list">
            {closest.map((c) => (
              <li key={c.code || c.description}>
                <button
                  type="button"
                  className={`ap-picker-opt${chosen?.description === c.description ? ' active' : ''}`}
                  disabled={busy}
                  onClick={() => void pickCandidate(c)}
                >
                  <span>{c.description}</span>
                  <span className="muted">{c.code} · scored {c.score}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="ap-picker-row">
        <label className="sched-label" htmlFor={`sq-${invoice.id}`}>Or search</label>
        <input
          id={`sq-${invoice.id}`}
          className="sched-input"
          type="search"
          value={q}
          disabled={busy}
          placeholder="Supplier name or code"
          onChange={(e) => { setQ(e.target.value); setChosen(null) }}
        />
        <span className="muted">{opts.length} match{opts.length === 1 ? '' : 'es'}</span>
      </div>

      {opts.length > 0 && (
        <ul className="ap-picker-list">
          {opts.map((o) => (
            <li key={o.contactAccountId}>
              <button
                type="button"
                className={`ap-picker-opt${chosen?.contactAccountId === o.contactAccountId ? ' active' : ''}`}
                disabled={busy}
                onClick={() => setChosen(o)}
              >
                <span>{o.description}</span>
                <span className="muted">{o.code}{o.defaultNominal ? ` · default nominal ${o.defaultNominal}` : ' · no default nominal'}</span>
              </button>
            </li>
          ))}
        </ul>
      )}

      <p className="sched-help">
        Set the nominal below. Whatever is set there is included when you tick Remember.
      </p>

      <div className="ap-picker-actions">
        <label className="ap-remember">
          <input type="checkbox" checked={remember} disabled={busy} onChange={(e) => setRemember(e.target.checked)} />
          Remember this supplier for {invoice.senderEmail ?? 'this name'}
        </label>
        <button className="btn ap-btn-go" type="button" disabled={busy || !chosen} onClick={() => void apply()}>
          {busy ? 'Saving…' : 'Use this supplier'}
        </button>
      </div>
    </div>
  )
}


/**
 * The nominal for one invoice.
 *
 * Deliberately its own control, shown on every in-flight row. It used to live
 * inside the supplier picker, which only appears when the supplier is NOT
 * confidently resolved — so the moment a supplier resolved cleanly (or a remembered
 * choice kicked in) the nominal box vanished and there was no way to set one at all.
 * Exactly backwards: a cleanly-resolved invoice is the normal case.
 *
 * One nominal applies to the whole invoice. Per-line coding is not supported — see
 * docs/AP-AUTO-FORWARD.md, Known simplification.
 */
function NominalControl({
  invoice, onChanged,
}: {
  invoice: Invoice
  onChanged: (inv: Invoice, events: ApEvent[]) => void
}) {
  const [code, setCode] = useState(invoice.nominalCode ?? '')
  const [q, setQ] = useState('')
  const [opts, setOpts] = useState<NominalOption[]>([])
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const [msg, setMsg] = useState<string | null>(null)

  useEffect(() => { setCode(invoice.nominalCode ?? '') }, [invoice.nominalCode])

  useEffect(() => {
    if (!q.trim()) { setOpts([]); return }
    const t = setTimeout(() => {
      void (async () => {
        try { setOpts((await searchNominals(q)).nominals) } catch { /* non-fatal */ }
      })()
    }, 250)
    return () => clearTimeout(t)
  }, [q])

  async function save(next: string | null) {
    setBusy(true); setErr(null); setMsg(null)
    try {
      const r = await setNominal(invoice.id, next)
      onChanged(r.invoice, r.events)
      setMsg(next ? `All lines will post to ${next}.` : 'Cleared — the supplier default or suspense 82110000 will be used.')
      setQ(''); setOpts([])
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="ap-nominal">
      <h4>Nominal for this invoice</h4>
      {err && <p className="alert alert--error">{err}</p>}
      {msg && <p className="alert alert--ok">{msg}</p>}

      <div className="ap-picker-row">
        <input
          className="sched-input sched-input--num"
          type="text"
          value={code}
          disabled={busy}
          placeholder="e.g. 82110000"
          onChange={(e) => setCode(e.target.value)}
        />
        <button className="btn" type="button" disabled={busy} onClick={() => void save(code.trim() || null)}>
          {busy ? 'Saving…' : 'Save nominal'}
        </button>
        {invoice.nominalCode && (
          <button className="btn" type="button" disabled={busy} onClick={() => void save(null)}>Clear</button>
        )}
        <input
          className="sched-input"
          type="search"
          value={q}
          disabled={busy}
          placeholder="search the chart of accounts"
          onChange={(e) => setQ(e.target.value)}
        />
      </div>

      {opts.length > 0 && (
        <ul className="ap-picker-list ap-picker-list--nom">
          {opts.map((n) => (
            <li key={n.code}>
              <button type="button" className="ap-picker-opt" disabled={busy} onClick={() => { setCode(n.code); setQ(''); setOpts([]) }}>
                <span className="mono">{n.code}</span>
                <span className="muted">{n.description}</span>
              </button>
            </li>
          ))}
        </ul>
      )}

      <p className="sched-help">
        Leave it blank to use the supplier default, or suspense 82110000 when they have none.
        This nominal applies to every line — per-line coding is not supported.
      </p>
    </div>
  )
}

// ─── row detail ──────────────────────────────────────────────────────────────

function RowDetail({
  invoice, onChanged,
}: {
  invoice: Invoice
  onChanged: (inv: Invoice, events: ApEvent[]) => void
}) {
  const [events, setEvents] = useState<ApEvent[] | null>(null)
  const [busy, setBusy] = useState<string | null>(null)
  const [err, setErr] = useState<string | null>(null)
  const [msg, setMsg] = useState<string | null>(null)
  const [existingDoc, setExistingDoc] = useState('')
  const [reason, setReason] = useState('')

  useEffect(() => {
    let cancelled = false
    void (async () => {
      try {
        const r = await getInvoice(invoice.id)
        if (!cancelled) setEvents(r.events)
      } catch (e) {
        if (!cancelled) setErr(e instanceof Error ? e.message : String(e))
      }
    })()
    return () => { cancelled = true }
  }, [invoice.id])

  async function run(what: string, fn: () => Promise<{ invoice: Invoice; events: ApEvent[] }>) {
    setBusy(what); setErr(null); setMsg(null)
    try {
      const r = await fn()
      setEvents(r.events)
      onChanged(r.invoice, r.events)
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(null)
    }
  }

  const p = invoice.proposal
  const r = invoice.supplierResolution
  const canSend = invoice.state === 'InFlight' && !invoice.forwardedUtc

  return (
    <div className="ap-detail">
      {err && <p className="alert alert--error">{err}</p>}
      {msg && <p className="alert alert--ok">{msg}</p>}

      {/* The proposal. Judging "ok to send" needs the iplicit supplier and its
          score, not just the four headline fields - an ambiguous supplier is the
          commonest blocker and the most expensive thing to get wrong. */}
      <section className="ap-proposal">
        <h4>What the app read, and where it would post it</h4>
        <div className="ap-proposal-grid">
          <dl className="ap-facts">
            <dt>Supplier on page</dt><dd>{p?.supplier ?? <span className="muted">not read</span>}</dd>
            <dt>Invoice no</dt><dd className="mono">{p?.theirDocNo ?? invoice.theirDocNo ?? '-'}</dd>
            <dt>Invoice date</dt><dd>{p?.invoiceDate ?? <span className="muted">not read</span>}</dd>
            <dt>Net / VAT / Gross</dt>
            <dd className="mono">{money(p?.netTotal ?? null)} / {money(p?.vatTotal ?? null)} / {money(p?.grossTotal ?? invoice.gross)}</dd>
          </dl>
          <dl className="ap-facts">
            <dt>iplicit supplier</dt>
            <dd>
              {!r ? <span className="muted">not looked up</span>
                : r.status === 'confident'
                  ? <><strong>{r.name}</strong> <span className="muted">score {r.score?.toFixed(2)}</span></>
                  : <span className="ap-unresolved">{r.status === 'ambiguous' ? 'ambiguous - refusing to guess' : 'no match found'}</span>}
            </dd>
            {r && r.status !== 'confident' && r.candidates.length > 0 && (
              <>
                <dt>Closest</dt>
                <dd className="muted">pick one below</dd>
              </>
            )}
            <dt>Looked up</dt>
            <dd>{invoice.resolvedUtc ? <span title={stamp(invoice.resolvedUtc).full}>{since(invoice.resolvedUtc)} ago</span> : <span className="muted">never</span>}</dd>
          </dl>
        </div>

        {p && p.lines.length > 0 && (
          <details className="ap-lines">
            <summary>{p.lines.length} line(s) read off the invoice</summary>
            <table className="table">
              <thead><tr><th>Description</th><th className="ap-right">Qty</th><th className="ap-right">Net</th></tr></thead>
              <tbody>
                {p.lines.map((l, ix) => (
                  <tr key={ix}>
                    <td>{l.description ?? '-'}</td>
                    <td className="ap-right">{l.quantity ?? '-'}</td>
                    <td className="ap-right mono">{money(l.net)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </details>
        )}

        {invoice.state === 'InFlight' && invoice.supplierResolution?.status !== 'confident' && (
          <SupplierPicker invoice={invoice} onChanged={onChanged} />
        )}

        {invoice.state === 'InFlight' && <NominalControl invoice={invoice} onChanged={onChanged} />}

        {invoice.supplierSetBy && (
          <p className="ap-chosen">
            iplicit supplier chosen by hand by <strong>{invoice.supplierSetBy}</strong>
            {invoice.nominalCode && <> · nominal <span className="mono">{invoice.nominalCode}</span></>}
          </p>
        )}

        <div className="ap-review-bar">
          <div className="ap-review-field">
            <label className="sched-label" htmlFor={`v-${invoice.id}`}>Status</label>
            <select
              id={`v-${invoice.id}`}
              className="select"
              disabled={busy !== null}
              value={invoice.verdict ?? ''}
              onChange={(e) => void run('verdict', () => patchReview(invoice.id, { verdict: e.target.value as Verdict }))}
            >
              <option value="" disabled>not assessed</option>
              <option value="ready">ok to send</option>
              <option value="needs_review">needs review</option>
              <option value="unreadable">cannot be read</option>
              <option value="needs_fix">needs a code fix</option>
            </select>
          </div>

          <div className="ap-review-field">
            <label className="sched-label" htmlFor={`c-${invoice.id}`}>Reason</label>
            <select
              id={`c-${invoice.id}`}
              className="select"
              disabled={busy !== null}
              value={invoice.failCategory ?? ''}
              onChange={(e) => void run('cat', () => patchReview(invoice.id, { category: e.target.value || null }))}
            >
              <option value="">none</option>
              {FAIL_CATEGORIES.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
            </select>
          </div>

          <div className="ap-review-field ap-review-note">
            <label className="sched-label" htmlFor={`n-${invoice.id}`}>Note</label>
            <input
              id={`n-${invoice.id}`}
              className="sched-input"
              type="text"
              placeholder="What is odd about this one?"
              defaultValue={invoice.reviewNote ?? ''}
              disabled={busy !== null}
              onBlur={(e) => {
                const v = e.target.value.trim()
                if (v && v !== (invoice.reviewNote ?? '')) {
                  void run('note', () => patchReview(invoice.id, { note: v }))
                }
              }}
            />
          </div>
        </div>

        <div className="ap-review-actions">
          <button
            className="btn ap-btn-go"
            type="button"
            disabled={busy !== null || !canSend}
            title={canSend
              ? 'Forward this one invoice to iplicit now, whatever mode the app is in'
              : invoice.forwardedUtc ? 'Already forwarded' : 'Nothing to send'}
            onClick={() => void run('send', async () => {
              const res = await sendInvoice(invoice.id)
              setMsg(res.message)
              return res
            })}
          >
            {busy === 'send' ? 'Sending...' : 'Send to iplicit'}
          </button>

          <button
            className="btn"
            type="button"
            disabled={busy !== null || invoice.state === 'Confirmed'}
            title="Re-read the invoice and work out the status again - press this after a fix"
            onClick={() => void run('recheck', async () => {
              const res = await recheckInvoice(invoice.id)
              setMsg('Re-read: ' + VERDICT_LABELS[res.verdict])
              return res
            })}
          >
            {busy === 'recheck' ? 'Re-reading...' : 'Check again'}
          </button>

          {invoice.sentManuallyBy && <span className="muted">sent by hand by {invoice.sentManuallyBy}</span>}
        </div>

        {/* Already in iplicit. Not the same thing as Ignore: Ignore means
            "deliberately skipped", discards the document number that proves the
            invoice is in, and counts as a failure it is not. Common during the
            move off InfoHub, and for a while after. */}
        {canSend && (
          <div className="ap-existing">
            <p className="ap-existing-lead">
              {invoice.iplicitDocNo
                ? <>This invoice looks like it is <strong>already in iplicit</strong> as <span className="mono">{invoice.iplicitDocNo}</span>. Check that document, then link it rather than sending a duplicate.</>
                : <>Already entered in iplicit by someone else? Link it to its document instead of ignoring it, so the proof is kept.</>}
            </p>
            <div className="ap-existing-row">
              <label className="sched-label" htmlFor={`x-${invoice.id}`}>iplicit doc</label>
              <input
                id={`x-${invoice.id}`}
                className="sched-input sched-input--num"
                type="text"
                placeholder="e.g. PI0012345"
                defaultValue={invoice.iplicitDocNo ?? ''}
                disabled={busy !== null}
                onChange={(e) => setExistingDoc(e.target.value)}
              />
              <button
                className="btn"
                type="button"
                disabled={busy !== null}
                onClick={() => {
                  const doc = (existingDoc || invoice.iplicitDocNo || '').trim()
                  if (!doc) { setErr('Enter the iplicit document number first.'); return }
                  void run('link', async () => {
                    const res = await linkExisting(invoice.id, doc)
                    setMsg(`Linked to ${doc} and filed. Recorded as already in iplicit, not as something we sent.`)
                    return res
                  })
                }}
              >
                {busy === 'link' ? 'Linking…' : 'Already in iplicit'}
              </button>
            </div>
          </div>
        )}
      </section>

      <div className="ap-detail-cols">
        <section>
          <h4>Timeline</h4>
          {events === null && <p className="muted">Loading…</p>}
          {events?.length === 0 && <p className="muted">No events recorded.</p>}
          {events && events.length > 0 && (
            <ol className="ap-timeline">
              {events.map((e, idx) => (
                <li key={`${e.at}-${idx}`} data-kind={e.kind}>
                  <span className="ap-tl-when" title={stamp(e.at).full}>{stamp(e.at).short}</span>
                  <span className="ap-tl-what">{EVENT_LABELS[e.kind] ?? e.kind}</span>
                  {e.actor !== 'system' && <span className="ap-tl-who">{e.actor}</span>}
                  {e.detail && <span className="ap-tl-detail">{e.detail}</span>}
                </li>
              ))}
            </ol>
          )}
        </section>

        <section>
          <h4>Facts</h4>
          <dl className="ap-facts">
            <dt>State</dt><dd>{invoice.state}{invoice.dryRun ? ' (dry run)' : ''}</dd>
            <dt>Extracted</dt>
            <dd>
              {invoice.docType ?? 'unknown type'}
              {invoice.confidence !== null && ` · confidence ${(invoice.confidence * 100).toFixed(0)}%`}
            </dd>
            <dt>iplicit doc</dt><dd className="mono">{invoice.iplicitDocNo ?? 'none'}</dd>
            <dt>Expected folder</dt><dd>{invoice.folderExpectedName ?? '—'}</dd>
            <dt>Actual folder</dt>
            <dd className={invoice.placementMismatch ? 'ap-mismatch' : undefined}>
              {invoice.folderActualName ?? 'not checked'}
              {invoice.folderCheckedUtc && <span className="muted"> · {since(invoice.folderCheckedUtc)} ago</span>}
            </dd>
            <dt>Message id</dt><dd className="mono ap-msgid">{invoice.immutableMessageId}</dd>
          </dl>

          <div className="ap-detail-actions">
            <button className="btn" type="button" disabled={busy !== null}
              onClick={() => void run('verify', () => verifyPlacement(invoice.id))}>
              {busy === 'verify' ? 'Checking…' : 'Where is it really?'}
            </button>

            {invoice.state === 'Ignored' ? (
              <button className="btn" type="button" disabled={busy !== null}
                onClick={() => void run('unignore', () => unignoreInvoice(invoice.id))}>
                {busy === 'unignore' ? 'Working…' : 'Un-ignore'}
              </button>
            ) : (
              <>
                <input className="sched-input" type="text" placeholder="Reason (optional)"
                  value={reason} disabled={busy !== null} onChange={(e) => setReason(e.target.value)} />
                <button className="btn" type="button" disabled={busy !== null}
                  onClick={() => void run('ignore', () => ignoreInvoice(invoice.id, reason))}>
                  {busy === 'ignore' ? 'Working…' : 'Ignore this invoice'}
                </button>
              </>
            )}
          </div>
        </section>
      </div>
    </div>
  )
}

// ─── page ────────────────────────────────────────────────────────────────────

export default function ApAutoForward() {
  const [settings, setSettings] = useState<ApSettings | null>(null)
  const [counts, setCounts] = useState<Counts | null>(null)
  const [job, setJob] = useState<ReconcileStatus | null>(null)
  const [categories, setCategories] = useState<CategoryCount[]>([])
  const [running, setRunning] = useState(false)
  const [runMsg, setRunMsg] = useState<string | null>(null)
  const [mailboxName, setMailboxName] = useState('')
  const [tab, setTab] = useState<TabKey>('needs')
  const [rows, setRows] = useState<Invoice[]>([])
  const [q, setQ] = useState('')
  const [loading, setLoading] = useState(true)
  const [rowsLoading, setRowsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [open, setOpen] = useState<number | null>(null)
  const [showSettings, setShowSettings] = useState(false)

  const loadOverview = useCallback(async () => {
    try {
      const r = await getOverview()
      setSettings(r.settings)
      setCounts(r.counts)
      setJob(r.job)
      setCategories(r.categories ?? [])
      setMailboxName(r.mailbox)
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setLoading(false)
    }
  }, [])

  const loadRows = useCallback(async (which: TabKey, search: string) => {
    setRowsLoading(true)
    setError(null)
    try {
      if (which === 'needs') {
        setRows((await getNeedsAttention()).invoices)
      } else if (which === 'inject') {
        // The inject path is "went past the window", which spans Confirmed and
        // AutoFailed, so it is a filter rather than a state.
        const all = (await getInvoices({ q: search })).invoices
        setRows(all.filter((i) => i.failedUtc !== null || i.injectedUtc !== null))
      } else if (which === 'all') {
        setRows((await getInvoices({ q: search })).invoices)
      } else {
        const state = which === 'inflight' ? 'InFlight' : which === 'confirmed' ? 'Confirmed' : which === 'failed' ? 'AutoFailed' : 'Ignored'
        setRows((await getInvoices({ state, q: search })).invoices)
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setRowsLoading(false)
    }
  }, [])

  useEffect(() => { void loadOverview() }, [loadOverview])
  useEffect(() => { void loadRows(tab, q) }, [tab, loadRows, q])

  const active = useMemo(() => TABS.find((t) => t.key === tab) ?? TABS[0], [tab])
  const windowMinutes = settings?.windowMinutes ?? 180

  async function toggle(field: 'enabled' | 'injectEnabled', value: boolean) {
    try {
      const r = await patchSettings({ [field]: value })
      setSettings(r.settings)
      setCounts(r.counts)
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    }
  }

  async function setMode(field: 'mode' | 'injectMode', value: ApMode) {
    try {
      const r = await patchSettings({ [field]: value })
      setSettings(r.settings)
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    }
  }

  const onRowChanged = (inv: Invoice) => {
    setRows((list) => list.map((x) => (x.id === inv.id ? inv : x)))
    void loadOverview()
  }

  if (loading) return <div className="panel"><p className="muted">Loading…</p></div>

  return (
    <div className="ap-page">
      <div className="panel">
        <div className="panel__head">
          <div>
            <h2>iplicit Auto Forward</h2>
            <p className="panel__sub">
              Drag a supplier invoice into <strong>{settings?.folders.source.name}</strong> in{' '}
              <span className="mono">{mailboxName}</span> and it gets forwarded to iplicit, chased until it is
              proven, and filed. Nothing in the Inbox is touched.
            </p>
          </div>
          <div className="ap-head-actions">
            <button
              className="btn"
              type="button"
              disabled={running || !settings?.enabled}
              title={!settings?.enabled ? 'Turn the master switch on first' : 'Read the folder and chase iplicit now, without waiting for the next slot'}
              onClick={() => void (async () => {
                setRunning(true); setRunMsg(null); setError(null)
                try {
                  const r = await runNow()
                  setRunMsg(r.lastRun.message)
                  await loadOverview()
                  await loadRows(tab, q)
                } catch (e) {
                  setError(e instanceof Error ? e.message : String(e))
                } finally {
                  setRunning(false)
                }
              })()}
            >
              {running ? 'Checking…' : 'Check now'}
            </button>
            <button className="btn" type="button" onClick={() => { void loadOverview(); void loadRows(tab, q) }}>Refresh</button>
            <button className="btn" type="button" onClick={() => setShowSettings(true)}>Settings</button>
          </div>
        </div>

        {error && <p className="alert alert--error">{error}</p>}
        {runMsg && <p className="alert alert--ok"><strong>Last check:</strong> {runMsg}</p>}

        {/* The job runs every 15 minutes. Without saying so, "I pressed Start and
            nothing happened" is the obvious first experience. */}
        {job && settings?.enabled && (
          <p className="ap-jobline">
            {job.enabled
              ? <>Checks every 15 minutes · last {job.lastRunUtc ? <span title={stamp(job.lastRunUtc).full}>{since(job.lastRunUtc)} ago</span> : 'never'} · next <strong>{until(job.nextRunUtc)}</strong></>
              : <><strong>The reconcile job is paused on the Scheduler tab</strong> — nothing will run until it is resumed.</>}
            {job.lastMessage && <span className="muted"> — {job.lastMessage}</span>}
          </p>
        )}

        {settings && !settings.enabled && (
          <p className="alert ap-alert-off">
            <strong>Switched off.</strong> Nothing is being read, forwarded or filed. Turn it on below when you are ready.
          </p>
        )}
        {settings?.enabled && settings.mode === 'review' && (
          <p className="alert ap-alert-review">
            <strong>Review mode.</strong> Every invoice is read, its supplier looked up in iplicit and a status
            proposed, but nothing is sent until you press Send to iplicit on the row. This is the mode for snagging.
          </p>
        )}
        {settings?.enabled && settings.mode === 'dry_run' && (
          <p className="alert ap-alert-dry">
            <strong>Dry run.</strong> The app will decide what it would do and record it, but will not forward or move any mail.
          </p>
        )}

        <div className="ap-controls">
          <div className="ap-switch">
            <span className="ap-switch-label">Master</span>
            <button className="btn" type="button" onClick={() => void toggle('enabled', !settings?.enabled)}>
              {settings?.enabled ? 'Stop' : 'Start'}
            </button>
            <select className="select" value={settings?.mode ?? 'dry_run'} onChange={(e) => void setMode('mode', e.target.value as ApMode)}>
              <option value="dry_run">dry run</option>
              <option value="review">review</option>
              <option value="live">live</option>
            </select>
          </div>
          <div className="ap-switch">
            <span className="ap-switch-label">Direct create</span>
            <button className="btn" type="button" onClick={() => void toggle('injectEnabled', !settings?.injectEnabled)}>
              {settings?.injectEnabled ? 'Disable' : 'Enable'}
            </button>
            <select className="select" value={settings?.injectMode ?? 'dry_run'} onChange={(e) => void setMode('injectMode', e.target.value as ApMode)}>
              <option value="dry_run">dry run</option>
              <option value="live">live</option>
            </select>
          </div>
        </div>

        {counts && (
          <div className="ap-stats">
            <div className="ap-stat" data-alarm={counts.needsAttention > 0}>
              <span className="ap-stat-label">Needs attention</span>
              <span className="ap-stat-value">{counts.needsAttention}</span>
            </div>
            <div className="ap-stat">
              <span className="ap-stat-label">In flight</span>
              <span className="ap-stat-value">{counts.inFlight}</span>
              {counts.oldestInFlightUtc && <span className="ap-stat-sub">oldest {since(counts.oldestInFlightUtc)}</span>}
            </div>
            <div className="ap-stat"><span className="ap-stat-label">Confirmed</span><span className="ap-stat-value">{counts.confirmed}</span></div>
            <div className="ap-stat" data-alarm={counts.autoFailed > 0}><span className="ap-stat-label">Auto failed</span><span className="ap-stat-value">{counts.autoFailed}</span></div>
            <div className="ap-stat"><span className="ap-stat-label">Ignored</span><span className="ap-stat-value">{counts.ignored}</span></div>
            <div className="ap-stat"><span className="ap-stat-label">Give up after</span><span className="ap-stat-value">{windowMinutes}m</span></div>
          </div>
        )}
      </div>

      {categories.length > 0 && (
        <div className="panel">
          <div className="panel__head">
            <div>
              <h2>Why invoices are failing</h2>
              <p className="panel__sub">
                Counted so the commonest class gets fixed first, rather than fixing invoices one at a time.
              </p>
            </div>
          </div>
          <ul className="ap-catlist">
            {categories.map((c) => (
              <li key={c.category}>
                <span className="ap-cat-n">{c.n}</span>
                <span>{FAIL_CATEGORIES.find((f) => f.key === c.category)?.label ?? c.category}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="panel">
        <nav className="ap-tabs" aria-label="Invoice states">
          {TABS.map((t) => (
            <button key={t.key} type="button" className={`ap-tab${t.key === tab ? ' active' : ''}`} onClick={() => { setTab(t.key); setOpen(null) }}>
              {t.label}
              {t.key === 'needs' && counts && counts.needsAttention > 0 && <span className="ap-tab-count">{counts.needsAttention}</span>}
            </button>
          ))}
        </nav>

        <div className="ap-toolbar">
          <p className="panel__sub ap-blurb">{active.blurb}</p>
          <input className="sched-input" type="search" placeholder="Supplier, invoice no or sender"
            value={q} onChange={(e) => setQ(e.target.value)} />
        </div>

        {rowsLoading && <p className="muted">Loading…</p>}

        {!rowsLoading && rows.length === 0 && (
          <div className="empty">
            <p>{tab === 'needs' ? 'Nothing needs your attention.' : 'Nothing here.'}</p>
            <p className="muted">
              {tab === 'needs'
                ? 'No stuck invoices, no failures, and every email is where it should be.'
                : `No invoices in this state yet.`}
            </p>
          </div>
        )}

        {!rowsLoading && rows.length > 0 && (
          <div className="ap-table-wrap">
            <table className="ap-table">
              <thead>
                <tr>
                  <th className="ap-th-expand" aria-label="Expand" />
                  {active.cols.map((c) => (
                    <th key={c.key} className={`${c.align === 'right' ? 'ap-right' : ''} ${c.wide ? 'ap-wide' : ''}`}>{c.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((inv) => {
                  const u = urgency(inv, windowMinutes)
                  return (
                    <Fragment key={inv.id}>
                      <tr data-urgency={u} data-ignored={inv.state === 'Ignored'}>
                        <td className="ap-td-expand">
                          <button className="ap-expand" type="button"
                            aria-expanded={open === inv.id}
                            aria-label={open === inv.id ? 'Hide detail' : 'Show detail'}
                            onClick={() => setOpen(open === inv.id ? null : inv.id)}>
                            {open === inv.id ? '−' : '+'}
                          </button>
                        </td>
                        {active.cols.map((c) => (
                          <td key={c.key} className={`${c.align === 'right' ? 'ap-right' : ''} ${c.wide ? 'ap-wide' : ''}`}>
                            {c.render(inv, { windowMinutes })}
                          </td>
                        ))}
                      </tr>
                      {open === inv.id && (
                        <tr className="ap-detail-row">
                          <td colSpan={active.cols.length + 1}>
                            <RowDetail invoice={inv} onChanged={onRowChanged} />
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showSettings && settings && (
        <SettingsDialog
          settings={settings}
          onClose={() => setShowSettings(false)}
          onSaved={(s) => setSettings(s)}
        />
      )}
    </div>
  )
}
